import React, { useContext, useEffect, useMemo, useState } from "react";
import "../../Tabels/Tabels.css";
import "./ProductAnalytics.css";
import Header from "../../Header/Header";

import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";

import toast, { Toaster } from "react-hot-toast";
import { Row, Col, Button, Table, Form } from "react-bootstrap";
import { Context } from "../../../utils/context";

import { useLoader } from "../../../utils/common";
import moment from "moment";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";


// ============================================================================================
// import { Form } from "react-bootstrap";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// ============================================================================================

library.add(fas);



const CATEGORIES = [
  "Spectacle Lenses",
  "Sunglasses",
  "Spectacle Frames",
  "Contact Lenses",
];

const CATEGORY_COLORS = {
  "Spectacle Lenses": "#4C6EF5",
  Sunglasses: "#F59F00",
  "Spectacle Frames": "#12B886",
  "Contact Lenses": "#E64980",
};

const GRAY_COLOR = "#adb5bd";

const getCurrentFinancialYear = () => {
  const now = moment();

  return now.month() < 3 ? now.year() - 1 : now.year();
};

const buildFinancialYearMonths = (fyStartYear) => {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const dt = moment({ year: fyStartYear, month: 3, day: 1 }).add(i, "months");
    months.push({ key: dt.format("YYYY-MM"), label: dt.format("MMM YYYY") });
  }
  return months;
};


const getFinancialYearOptions = (yearsBack = 6, yearsAhead = 3) => {
  const currentFY = getCurrentFinancialYear();
  const options = [];
  for (let fy = currentFY + yearsAhead; fy >= currentFY - yearsBack; fy--) {
    options.push({ value: fy, label: `FY ${fy}-${String(fy + 1).slice(-2)}` });
  }
  return options;
};




const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const values = Object.values(value);
    if (values.length && values.every((v) => v && typeof v === "object")) return values;
  }
  return [];
};

const getCategoryLabel = (product) =>
  product?.p_category?.name ||
  product?.p_category_id?.label ||
  product?.category_name ||
  product?.p_category_name ||
  null;

const getOrderDetails = (product) => {
  const details = product?.Product_Order_Details || product?.product_order_details || [];
  return Array.isArray(details) ? details : [];
};

const getOrderQuantity = (detail) =>
  parseFloat(detail?.quantity ?? detail?.qty ?? detail?.order_quantity ?? 1) || 0;


const ProductAnalytics = () => {
  const { getData } = useContext(Context);
  const { withLoader } = useLoader();

  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const handleCategoryButtonClick = (label) => {
    setSelectedCategory((prev) => (prev === label ? null : label));
  };

  // Financial year currently driving the chart / details table
  const [selectedYear, setSelectedYear] = useState(getCurrentFinancialYear());
  const yearOptions = useMemo(() => getFinancialYearOptions(6, 0), []);

 
  const [apiCategories, setApiCategories] = useState([]); 
  const [products, setProducts] = useState([]); 

  const [purchaseSummary, setPurchaseSummary] = useState([]);
  const [poUnavailable, setPoUnavailable] = useState(false);
  
  const [chartClick, setChartClick] = useState(null);

  
  const [purchaseDetails, setPurchaseDetails] = useState([]);
  const [detailsUnavailable, setDetailsUnavailable] = useState(false);

  
  const [categoryProductCounts, setCategoryProductCounts] = useState([]); 
  const [productCountsUnavailable, setProductCountsUnavailable] = useState(false);

  const monthWindow = useMemo(() => buildFinancialYearMonths(selectedYear), [selectedYear]);

  // ============================================================================================
  // DISABLED FILTERS — state kept for when filters are re-enabled (point 3)
  // ============================================================================================
  // const emptyFilters = {
  //   brand: "",
  //   color: "",
  //   shape: "",
  //   modelNo: "",
  //   size: "",
  //   priceMin: "",
  //   priceMax: "",
  //   productName: "",
  //   startDate: null,
  //   endDate: null,
  // };
  // const [filters, setFilters] = useState(emptyFilters);
  // const [brandOptions, setBrandOptions] = useState([]);
  // const [colorOptions, setColorOptions] = useState([]);
  // const [shapeOptions, setShapeOptions] = useState([]);
  // ============================================================================================

 
  const matchedCategories = useMemo(() => {
    const apiCats = Array.isArray(apiCategories) ? apiCategories : [];
    return CATEGORIES.map((targetLabel) => {
      const found = apiCats.find(
        (c) => String(c.label || "").trim().toLowerCase() === targetLabel.toLowerCase()
      );
      return { label: targetLabel, value: found?.value ?? null };
    });
  }, [apiCategories]);

  const categoryIdFor = (label) => {
    if (!label) return null;
    return matchedCategories.find((c) => c.label === label)?.value ?? null;
  };



  const getAllCategories = async () => {
    try {
      const response = await getData("/common/masters/all-vendor-p-categories");
      if (response?.success) {
        const opts = toArray(response?.data)
          .map((c) => ({ value: c?.id ?? c?.p_category_id ?? c?.value, label: c?.name ?? c?.label }))
          .filter((c) => c.value != null && c.label);
        setApiCategories(opts);
      } else {
        setApiCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setApiCategories([]);
    }
  };

  
  const getAllProducts = async () => {
    try {
      const response = await withLoader(() => getData("/admin/products?per_page=1000"));
      if (!response?.success) {
        setProducts([]);
        return;
      }

      let productRows = [];
      if (Array.isArray(response?.data?.rows)) productRows = response.data.rows;
      else if (Array.isArray(response?.data)) productRows = response.data;
      else if (Array.isArray(response?.rows)) productRows = response.rows;
      else if (Array.isArray(response?.data?.data)) productRows = response.data.data;
      else if (Array.isArray(response?.result)) productRows = response.result;

      setProducts(Array.isArray(productRows) ? productRows : []);
    } catch (error) {
      console.error("Error fetching products for analytics:", error);
      setProducts([]);
    }
  };


  const getProductCountsData = async () => {
    try {
      const response = await getData(`/admin/dashboard/product-counts`);
      if (response?.success) {
        setCategoryProductCounts(toArray(response?.data));
        setProductCountsUnavailable(false);
      } else {
        setCategoryProductCounts([]);
        setProductCountsUnavailable(true);
      }
    } catch (error) {
      console.warn("Product count endpoint unavailable:", error);
      setCategoryProductCounts([]);
      setProductCountsUnavailable(true);
    }
  };

  useEffect(() => {
    getAllCategories();
    getAllProducts();
    getProductCountsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const categoryProductTotals = useMemo(() => {
    return CATEGORIES.map((label) => {
      const apiId = categoryIdFor(label);
      const row = categoryProductCounts.find(
        (r) => apiId != null && Number(r?.category_id) === Number(apiId)
      );
      return { label, total: row ? Number(row?.Product_count) || 0 : 0 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryProductCounts, matchedCategories]);

   

  const bestLeastByCategory = useMemo(() => {
    const productList = Array.isArray(products) ? products : [];

    return CATEGORIES.map((label) => {
      const categoryProducts = productList.filter((p) => getCategoryLabel(p) === label);

      const withQty = categoryProducts
        .map((product) => ({
          name: product?.name || product?.product_name || "Unnamed Product",
          qty: getOrderDetails(product).reduce((sum, d) => sum + getOrderQuantity(d), 0),
        }))
        .filter((p) => p.qty > 0);

      const best = withQty.length ? [...withQty].sort((a, b) => b.qty - a.qty)[0] : null;
      const least = withQty.length ? [...withQty].sort((a, b) => a.qty - b.qty)[0] : null;

      return { label, best, least };
    });
  }, [products]);


  const getPurchaseSummaryData = async () => {
    try {
      const params = new URLSearchParams();
      params.append("year", selectedYear);

      const response = await getData(`/admin/dashboard/purchase-summary?${params.toString()}`);

      if (response?.success) {
        setPurchaseSummary(toArray(response?.data));
        setPoUnavailable(false);
      } else {
        setPurchaseSummary([]);
        setPoUnavailable(true);
      }
    } catch (error) {
      console.warn("Purchase summary endpoint unavailable:", error);
      setPurchaseSummary([]);
      setPoUnavailable(true);
    }
  };

 
  useEffect(() => {
    getPurchaseSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    setChartClick(null);
  }, [selectedCategory, selectedYear]);


  const chartCategories = CATEGORIES;

  
  const highlightCategory = chartClick?.category || selectedCategory || null;


  const chartData = useMemo(() => {
    return monthWindow.map(({ key, label }) => {
      const row = { month: key, label };
      chartCategories.forEach((catLabel) => {
        const match = purchaseSummary.find(
          (r) =>
            r?.order_month === key &&
            String(r?.["category name"] || "").trim().toLowerCase() === catLabel.toLowerCase()
        );
        row[catLabel] = match ? parseFloat(match?.total_quantity || 0) || 0 : 0;
      });
      return row;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseSummary, monthWindow]);


  const categoryYearTotals = useMemo(() => {
    return CATEGORIES.map((label) => ({
      label,
      total: Math.round(chartData.reduce((sum, row) => sum + (parseFloat(row[label]) || 0), 0) * 100) / 100,
    }));
  }, [chartData]);



  const effectiveCategory = chartClick?.category || selectedCategory || null;
  const effectiveMonth = chartClick?.month || null;

  const getPurchaseDetailsData = async () => {
    try {
      const categoryId = categoryIdFor(effectiveCategory);
      const params = new URLSearchParams();
      params.append("year", selectedYear);
      if (categoryId != null) params.append("category_id", categoryId);

      const response = await getData(`/admin/dashboard/purchase-details?${params.toString()}`);

      if (response?.success) {
        setPurchaseDetails(toArray(response?.data));
        setDetailsUnavailable(false);
      } else {
        setPurchaseDetails([]);
        setDetailsUnavailable(true);
      }
    } catch (error) {
      console.warn("Purchase details endpoint unavailable:", error);
      setPurchaseDetails([]);
      setDetailsUnavailable(true);
    }
  };

  useEffect(() => {
    getPurchaseDetailsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCategory, matchedCategories, selectedYear]);

 
  const filteredDetailRows = useMemo(() => {
    if (!effectiveMonth) return purchaseDetails;
    return purchaseDetails.filter((row) => {
      const rowMonth = moment(row?.order_month, "DD-MM-YYYY");
      return rowMonth.isValid() && rowMonth.format("YYYY-MM") === effectiveMonth;
    });
  }, [purchaseDetails, effectiveMonth]);

 
  const handleBarClick = (category, datum) => {
    const month = datum?.month;
    if (!month) return;

    setChartClick((prev) =>
      prev && prev.category === category && prev.month === month ? null : { category, month }
    );
  };

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value, 10));
  };

  const financialYearLabel = `FY ${selectedYear}-${String(selectedYear + 1).slice(-2)}`;

 
 return (
    <>
      <div className="main-advancedashboard product-analytics-page">
        <Header title="Product Analytics" link="/product-analytics" />

        <section className="AdvanceDashboard">
          <div className="col-xxl-12 col-xl-12 p-0">
            <div className="row MainRowsec me-0 ms-0">
              <section className="Tabels tab-radio">
                <div>
                 
                  <style>{`
                    .category-filter-card {
                      display: block;
                      width: 100%;
                      background: #ffffff;
                      color: #212529;
                      border: 1px solid #dee2e6;
                      border-radius: 10px;
                      padding: 14px 16px;
                      text-align: center;
                      cursor: pointer;
                      box-sizing: border-box;
                      transform: none;
                    }
                    .category-filter-card:hover {
                      border-color: #0d6efd;
                    }
                    .category-filter-card:focus {
                      outline: none;
                      border-color: #0d6efd;
                    }
                    .category-filter-card.active {
                      background: #0d6efd;
                      border-color: #0d6efd;
                    }
                    .category-filter-card .cat-label {
                      font-size: 13px;
                      font-weight: 600;
                      letter-spacing: 0.2px;
                    }
                    .category-filter-card .cat-count {
                      font-size: 22px;
                      font-weight: 700;
                      margin-top: 4px;
                    }
                    .category-filter-card.active .cat-label,
                    .category-filter-card.active .cat-count {
                      color: #ffffff;
                    }
                  `}</style>

                
                  <div className="panel-section category-section">
                    <Row className="mb-3 g-2">
                      {CATEGORIES.map((label) => {
                        const isActive = selectedCategory === label;
                        const total = categoryProductTotals.find((c) => c.label === label)?.total;
                        return (
                          <Col lg={3} md={6} sm={6} xs={6} key={label}>
                            <button
                              type="button"
                              className={`category-filter-card${isActive ? " active" : ""}`}
                              onClick={() => handleCategoryButtonClick(label)}
                              title={productCountsUnavailable ? "Product count endpoint unavailable" : undefined}
                            >
                              <div className="cat-label">{label}</div>
                              <div className="cat-count">{productCountsUnavailable ? "—" : total}</div>
                            </button>
                          </Col>
                        );
                      })}
                    </Row>

                    <div className="mb-0">
                      <Table bordered responsive size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th title="Ranking type">Type</th>
                            {CATEGORIES.map((label) => (
                              <th key={label} title={label}>
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td title="Highest quantity sold">Top Selling</td>
                            {bestLeastByCategory.map((c) => (
                              <td
                                key={c.label}
                                title={c.best ? `${c.best.name} — Qty: ${c.best.qty}` : "No sales"}
                              >
                                {c.best ? c.best.name : "—"}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td title="Lowest quantity sold">Least Selling</td>
                            {bestLeastByCategory.map((c) => (
                              <td
                                key={c.label}
                                title={c.least ? `${c.least.name} — Qty: ${c.least.qty}` : "No sales"}
                              >
                                {c.least ? c.least.name : "—"}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {/* ============================================================================================
                      FILTER PANEL — DISABLED 
                  ============================================================================================
                  <div className="filter-panel mb-4 p-3" style={{ border: "1px solid #e5e5e5", borderRadius: 8 }}>
                    <Row className="align-items-end">
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Brand</Form.Label>
                        <Form.Select value={filters.brand} disabled={!selectedCategory} onChange={(e) => handleFilterChange("brand", e.target.value)}>
                          <option value="">{selectedCategory ? "All Brands" : "Select a category first"}</option>
                          {brandOptions.map((b) => (<option key={b.value} value={b.value}>{b.label}</option>))}
                        </Form.Select>
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Color</Form.Label>
                        <Form.Select value={filters.color} disabled={!selectedCategory} onChange={(e) => handleFilterChange("color", e.target.value)}>
                          <option value="">{selectedCategory ? "All Colors" : "Select a category first"}</option>
                          {colorOptions.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                        </Form.Select>
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Shape</Form.Label>
                        <Form.Select value={filters.shape} disabled={!selectedCategory} onChange={(e) => handleFilterChange("shape", e.target.value)}>
                          <option value="">{selectedCategory ? "All Shapes" : "Select a category first"}</option>
                          {shapeOptions.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                        </Form.Select>
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Model No.</Form.Label>
                        <Form.Control type="text" placeholder="Model No." disabled={!selectedCategory} value={filters.modelNo} onChange={(e) => handleFilterChange("modelNo", e.target.value)} />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Size</Form.Label>
                        <Form.Control type="text" placeholder="Size" disabled={!selectedCategory} value={filters.size} onChange={(e) => handleFilterChange("size", e.target.value)} />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Product Name</Form.Label>
                        <Form.Control type="text" placeholder="Product Name" disabled={!selectedCategory} value={filters.productName} onChange={(e) => handleFilterChange("productName", e.target.value)} />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Price Min</Form.Label>
                        <Form.Control type="number" placeholder="Min Price" disabled={!selectedCategory} value={filters.priceMin} onChange={(e) => handleFilterChange("priceMin", e.target.value)} />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Price Max</Form.Label>
                        <Form.Control type="number" placeholder="Max Price" disabled={!selectedCategory} value={filters.priceMax} onChange={(e) => handleFilterChange("priceMax", e.target.value)} />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>Start Date</Form.Label>
                        <DatePicker className="form-control" selected={filters.startDate} onChange={(date) => handleFilterChange("startDate", date)} selectsStart startDate={filters.startDate} endDate={filters.endDate} placeholderText="Start Date" />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2">
                        <Form.Label>End Date</Form.Label>
                        <DatePicker className="form-control" selected={filters.endDate} onChange={(date) => handleFilterChange("endDate", date)} selectsEnd startDate={filters.startDate} endDate={filters.endDate} minDate={filters.startDate} placeholderText="End Date" />
                      </Col>
                      <Col lg={3} md={6} sm={12} className="mb-2 d-flex align-items-end">
                        <Button variant="outline-secondary" className="w-100" onClick={clearFilters}>Clear Filters</Button>
                      </Col>
                    </Row>
                  </div>
                  ============================================================================================ */}

                
                  <div className="panel-section chart-section">
                    <Row>
                      <Col lg={12} md={12} className="mb-0">
                        {poUnavailable ? (
                          <div
                            className="chart-panel p-3 d-flex align-items-center justify-content-center"
                            style={{ border: "1px solid #e5e5e5", borderRadius: 8, minHeight: 420 }}
                          >
                            <p className="no-datashow text-center mb-0">
                              Purchase summary endpoint isn't reachable.
                              <br />
                              Confirm <code>/admin/dashboard/purchase-summary</code> is wired up on the backend.
                            </p>
                          </div>
                        ) : (
                          <div className="chart-panel p-3" style={{ border: "1px solid #e5e5e5", borderRadius: 8, minHeight: 460 }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                              <h5 className="mb-0">Total Purchase Orders by Category</h5>
                              <Form.Select
                                size="sm"
                                value={selectedYear}
                                onChange={handleYearChange}
                                style={{ width: 170 }}
                                aria-label="Select financial year"
                              >
                                {yearOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 60 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fontSize: 10 }}
                                  interval={0}
                                  angle={-45}
                                  textAnchor="end"
                                  height={70}
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip formatter={(value, name) => [value, name]} />
                                <Legend />
                                {chartCategories.map((cat) => (
                                  <Bar
                                    key={cat}
                                    dataKey={cat}
                                    name={cat}
                                    barSize={14}
                                    minPointSize={2}
                                    cursor="pointer"
                                    fill={
                                      highlightCategory && highlightCategory !== cat
                                        ? GRAY_COLOR
                                        : CATEGORY_COLORS[cat] || "#4C6EF5"
                                    }
                                    onClick={(datum) => handleBarClick(cat, datum)}
                                  />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>

                            {purchaseSummary.length === 0 && (
                              <p className="text-muted text-center mt-2 mb-0">
                                No purchase order records were returned for {financialYearLabel}. If you
                                expect data here, verify that <code>purchase_orders</code> rows exist in this
                                date range and that the category id the page is sending matches the{" "}
                                <code>p_category_id</code> your products actually use.
                              </p>
                            )}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="panel-section details-section">
                    <h5 className="mb-3">
                      Purchase Details — {financialYearLabel}
                      {effectiveCategory
                        ? ` — ${effectiveCategory}${
                            effectiveMonth ? ` (${moment(effectiveMonth, "YYYY-MM").format("MMM YYYY")})` : ""
                          }`
                        : " (Summary — no category selected)"}
                    </h5>

                    {detailsUnavailable ? (
                      <p className="no-datashow">
                        Purchase details endpoint isn't reachable. Confirm{" "}
                        <code>/admin/dashboard/purchase-details</code> is wired up on the backend.
                      </p>
                    ) : effectiveCategory ? (
                      <Table bordered hover responsive size="sm">
                        <thead>
                          <tr>
                            <th title="Purchase date">Date</th>
                            <th title="Supplier name">Supplier</th>
                            <th title="Brand name">Brand</th>
                            <th title="Quantity purchased">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDetailRows.length > 0 ? (
                            filteredDetailRows.map((row, idx) => (
                              <tr key={idx}>
                                <td title={row?.order_month}>{row?.order_month}</td>
                                <td title={row?.supplier}>{row?.supplier}</td>
                                <td title={row?.Brands}>{row?.Brands}</td>
                                <td title={row?.total_quantity}>{row?.total_quantity}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center text-muted">
                                No purchase details found for this selection.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    ) : (
                     
                      <Table bordered size="sm" className="mb-0" style={{ maxWidth: 420 }}>
                        <thead>
                          <tr>
                            <th title="Category">Category</th>
                            <th title="Total quantity purchased">Total Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryYearTotals.map((c) => (
                            <tr key={c.label}>
                              <td title={c.label}>{c.label}</td>
                              <td title={`${c.label} — Qty: ${c.total}`}>{c.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </section>
      </div>

      <Toaster position="top-right" />
    </>
  );
};

export default ProductAnalytics;